import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { cloneDeep } from 'lodash';
import Decimal from 'big.js';
import FormFieldInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-interface';
import FormFieldSetInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-set-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import Service from '@/model/entity/billing/service';
import ServiceDto from '@/model/entity/billing/service-dto';

@Component
export default class ServiceForm extends Vue
{
    @Prop()
    private service!: Service;

    @Prop({
        type: Boolean,
        default: false,
    })
    private saving!: boolean;

    private form: FormInterface|null = null;

    private created()
    {
        this.generateForm();
    }

    @Watch('service')
    private onServiceChanged()
    {
        this.generateForm();
    }

    /**
     * Handles form saves
     *
     * @param form
     */

    private async onSave(form: FormInterface)
    {
        if (!this.service)
        {
            return;
        }

        // Generate a service from the form data
        const service = this.generateService(form);

        // Emit a save event with the service
        this.$emit('save', service);
    }

    /**
     * Generates the form from the service
     */

    private generateForm()
    {
        if (!this.service)
        {
            this.form = null;
            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    title: this.$t('general.field.basic_details').toString(),
                    fields: [
                        {
                            key: 'client',
                            type: 'integer',
                            title: this.$t('general.field.client').toString(),
                            value: this.service.clientId,
                            required: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.clientService.clientFormOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.clientService.clientFormTitlesCallback(values),
                            disableInitialOptionsCallback: true,
                            disableEmptySearchResults: true,
                        },
                        {
                            key: 'domain',
                            type: 'integer',
                            title: this.$t('service.field.domain').toString(),
                            value: this.service.domainId,
                            required: false,
                            optionsCallback: async(search: string) => this.$serviceContainer.domainService.domainFormOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.domainService.domainFormTitlesCallback(values),
                            clearable: true,
                            disableInitialOptionsCallback: true,
                            disableEmptySearchResults: true,
                        },
                        {
                            key: 'stage',
                            type: 'string',
                            title: this.$t('general.field.stage').toString(),
                            value: this.service.stage,
                            required: true,
                            options: [
                                {
                                    value: 'Cancelled',
                                    title: 'Cancelled',
                                },
                                {
                                    value: 'Let Expire',
                                    title: 'Let Expire',
                                },
                                {
                                    value: 'Renewed',
                                    title: 'Renewed',
                                },
                                {
                                    value: 'Enquiry',
                                    title: 'Enquiry',
                                },
                                {
                                    value: 'Enquiry Only',
                                    title: 'Enquiry Only',
                                },
                                {
                                    value: 'Quote Sent',
                                    title: 'Quote Sent',
                                },
                                {
                                    value: 'On Hold',
                                    title: 'On Hold',
                                },
                                {
                                    value: 'Complete',
                                    title: 'Complete',
                                },
                                {
                                    value: 'Set up',
                                    title: 'Set up',
                                },
                                {
                                    value: 'WEB',
                                    title: 'WEB',
                                },
                            ],
                            dropdown: true,
                        },
                        {
                            key: 'dateExpiry',
                            type: 'date',
                            title: this.$t('general.field.date_expiry').toString(),
                            value: this.service.dateExpiry,
                            required: false,
                        },
                        {
                            key: 'title',
                            type: 'string',
                            title: this.$t('general.field.title').toString(),
                            value: this.service.title,
                            required: true,
                        },
                        {
                            key: 'invoiceAmount',
                            type: 'decimal',
                            title: this.$t('service.field.invoice_amount').toString(),
                            value: this.service.invoiceAmount.toString(),
                            required: true,
                        },
                        {
                            key: 'disabled',
                            type: 'boolean',
                            title: this.$t('general.field.disabled').toString(),
                            value: this.service.disabled,
                            required: true,
                        },
                    ],
                    required: true,
                },
            ],
            stepper: true,
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Generates the service from the form
     */

    private generateService(form: FormInterface): Service
    {
        if (!this.service)
        {
            return new Service(new ServiceDto());
        }

        const service = new Service(cloneDeep(this.service.dto));

        this.addFieldValues(service, form.items);

        return service;
    }

    /**
     * Adds field values from form items to the passed service
     *
     * @param service
     * @param items
     */

    private addFieldValues(service: Service, items: (FormFieldSetInterface | FormFieldInterface)[])
    {
        for (const item of items)
        {
            if (!('key' in item))
            {
                this.addFieldValues(service, item.fields);
                continue;
            }

            switch (item.key)
            {
            case 'client':
                service.clientId = item.value as number;
                break;

            case 'domain':
                service.domainId = item.value as number;
                break;

            case 'stage':
                service.stage = item.value as string;
                break;

            case 'dateExpiry':
                service.dateExpiry = item.value as Date;
                break;

            case 'title':
                service.title = item.value as string;
                break;

            case 'invoiceAmount':
                service.invoiceAmount = new Decimal(item.value as string);
                break;

            case 'disabled':
                service.disabled = item.value as boolean;
                break;
            }
        }
    }
}